"use client";

import React, { useContext, useState } from "react";

import {
    CourierPrime,
    DMSans,
    Inter,
    Lato,
    LibreFranklin,
    Lora,
    MirandaSans,
    Montserrat,
    OpenSans,
    PlayfairDisplay,
    Poppins,
    Raleway,
    Roboto,
    RobotoCondensed,
    RobotoSerif,
    Rubik,
    SourceSansPro,
    Times,
    Ubuntu,
    SYSTEM_SEGOE_UI_CSS_FONT_FAMILY,
} from "../lib/fonts";
import { cn } from "@/lib/utils";

// MARK: FontEnum
export enum FontEnum {
    CourierPrime = "CourierPrime",
    DMSans = "DMSans",
    Inter = "Inter",
    Lato = "Lato",
    LibreFranklin = "LibreFranklin",
    Lora = "Lora",
    MirandaSans = "MirandaSans",
    Montserrat = "Montserrat",
    OpenSans = "OpenSans",
    PlayfairDisplay = "PlayfairDisplay",
    Poppins = "Poppins",
    Raleway = "Raleway",
    Roboto = "Roboto",
    RobotoCondensed = "RobotoCondensed",
    RobotoSerif = "RobotoSerif",
    Rubik = "Rubik",
    /** Windows Segoe UI via OS fonts (no bundled Segoe files). */
    Segoe = "Segoe",
    SourceSansPro = "SourceSansPro",
    Times = "Times",
    Ubuntu = "Ubuntu",
}

const FONT_ENUM_CLASS: Partial<Record<FontEnum, string>> = {
    [FontEnum.CourierPrime]: CourierPrime.className,
    [FontEnum.DMSans]: DMSans.className,
    [FontEnum.Inter]: Inter.className,
    [FontEnum.Lato]: Lato.className,
    [FontEnum.LibreFranklin]: LibreFranklin.className,
    [FontEnum.Lora]: Lora.className,
    [FontEnum.MirandaSans]: MirandaSans.className,
    [FontEnum.Montserrat]: Montserrat.className,
    [FontEnum.OpenSans]: OpenSans.className,
    [FontEnum.PlayfairDisplay]: PlayfairDisplay.className,
    [FontEnum.Poppins]: Poppins.className,
    [FontEnum.Raleway]: Raleway.className,
    [FontEnum.Roboto]: Roboto.className,
    [FontEnum.RobotoCondensed]: RobotoCondensed.className,
    [FontEnum.RobotoSerif]: RobotoSerif.className,
    [FontEnum.Rubik]: Rubik.className,
    [FontEnum.SourceSansPro]: SourceSansPro.className,
    [FontEnum.Times]: Times.className,
    [FontEnum.Ubuntu]: Ubuntu.className,
};

// MARK: font_context_wrapper_props
function font_context_wrapper_props(font: FontEnum): { className?: string; style?: React.CSSProperties } {
    if (font === FontEnum.Segoe) {
        return { style: { fontFamily: SYSTEM_SEGOE_UI_CSS_FONT_FAMILY } };
    }
    const next_class = FONT_ENUM_CLASS[font];
    return next_class != null ? { className: next_class } : {};
}

const FontContext = React.createContext<{ font: FontEnum; setFont: (font: FontEnum) => void }>({
    font: FontEnum.Lato,
    setFont: () => {},
});

// MARK: useFontContext
export function useFontContext() {
    return useContext(FontContext);
}

// MARK: FontContextProvider
export function FontContextProvider({ children }: { children: React.ReactNode }) {
    const [font, setFont] = useState<FontEnum>(FontEnum.Lato);
    const { className: font_wrapper_class, style: font_wrapper_style } = font_context_wrapper_props(font);

    return (
        <FontContext.Provider value={{ font, setFont }}>
            <div className={cn("h-screen w-screen", font_wrapper_class)} style={font_wrapper_style}>
                {children}
            </div>
        </FontContext.Provider>
    );
}
